import { CollapsibleSection } from "./CollapsibleSection.tsx";
import { ContactsPanel } from "./ContactsPanel.tsx";
import { Device360 } from "./Device360.tsx";
import { ScheduleWidget } from "./ScheduleWidget.tsx";
import { TasksPanel } from "./TasksPanel.tsx";
import { AssetDispatchPanel } from "./AssetDispatchPanel.tsx";
import { contactSummary, intakeSummary, scheduleSummary } from "../utils/ticketDisplay.ts";
import type { EmailLog, Ticket } from "../types.ts";
import type { MentionUser } from "../utils/messageDisplay.ts";
import type { ScheduleSaveResult } from "../types.ts";
import type { HasThreadMessage } from "../utils/threadMessage.ts";

export function TicketSidebar({
  ticket,
  emailLog,
  mentionUsers,
  canWrite,
  onSaveContacts,
  onScheduleSaved,
  onTasksChanged,
  onAssetChanged,
  onClose,
}: {
  ticket: Ticket;
  emailLog: EmailLog[];
  mentionUsers: MentionUser[];
  canWrite: boolean;
  onSaveContacts: (fields: { contact_emails: string[]; contact_phones: string[] }) => Promise<void>;
  onScheduleSaved: (result: ScheduleSaveResult) => void;
  onTasksChanged?: (result?: import("../types.ts").SupportTask) => void;
  onAssetChanged?: (result?: HasThreadMessage) => void;
  /** 情報レールを閉じる（改善 B-1） */
  onClose?: () => void;
}) {
  return (
    <aside className="support-ticket-detail__sidebar">
      {onClose && (
        <div className="support-ticket-detail__sidebar-head">
          <span>情報</span>
          <button type="button" onClick={onClose} aria-label="情報レールを閉じる" title="閉じる">×</button>
        </div>
      )}
      <div className="support-detail-sections">
        <CollapsibleSection title="タスク" summary={`未完了 ${ticket.open_task_count ?? 0} 件`} defaultOpen={(ticket.open_task_count ?? 0) > 0}>
          <TasksPanel
            ticketId={ticket.id}
            mentionUsers={mentionUsers}
            canWrite={canWrite}
            onChanged={onTasksChanged}
          />
        </CollapsibleSection>

        <CollapsibleSection title="連絡先" summary={contactSummary(ticket)}>
          <ContactsPanel ticket={ticket} embedded onSaved={onSaveContacts} />
        </CollapsibleSection>

        <CollapsibleSection title="機体手配" summary={ticket.unit_serial ? `本機 ${ticket.unit_serial}` : "未登録"} defaultOpen={!!ticket.unit_serial}>
          <AssetDispatchPanel ticket={ticket} canWrite={canWrite} onChanged={onAssetChanged} />
        </CollapsibleSection>

        <CollapsibleSection
          title="現場予定"
          summary={scheduleSummary({
            scheduled_at: ticket.scheduled_at,
            scheduled_tech: ticket.scheduled_tech,
            visit_status: ticket.visit_status,
          })}
        >
          <ScheduleWidget
            ticketId={ticket.id}
            embedded
            cur={{
              scheduled_at: ticket.scheduled_at,
              scheduled_tech: ticket.scheduled_tech,
              visit_status: ticket.visit_status,
              schedule_note: ticket.schedule_note,
            }}
            onSaved={onScheduleSaved}
          />
        </CollapsibleSection>

        {ticket.device_mac && <Device360 mac={ticket.device_mac} />}

        {(ticket.description || (ticket.attachments || []).length > 0) && (
          <CollapsibleSection title="起票内容" summary={intakeSummary(ticket)}>
            {ticket.description && (
              <div className="support-ticket-detail__sidebar-desc">{ticket.description}</div>
            )}
            {(ticket.attachments || []).length > 0 && (
              <div className="support-ticket-detail__attachments">
                {(ticket.attachments || []).map((a) =>
                  a.type === "image" ? (
                    <a key={a.path} href={a.url} target="_blank" rel="noopener noreferrer">
                      <img src={a.url} alt={a.name} />
                    </a>
                  ) : a.type === "video" ? (
                    <a key={a.path} href={a.url} target="_blank" rel="noopener noreferrer" className="support-att-video">
                      🎬 {a.name || "動画"}
                    </a>
                  ) : (
                    <a key={a.path} href={a.url} target="_blank" rel="noopener noreferrer" className="support-att-video">
                      📎 {a.name || "ファイル"}
                    </a>
                  ),
                )}
              </div>
            )}
          </CollapsibleSection>
        )}

        {emailLog.length > 0 && (
          <CollapsibleSection title="メール配送ログ" summary={`${emailLog.length}件`}>
            <div className="support-email-log">
              {emailLog.map((e) => {
                const ok = e.status === "sent" || e.status === "delivered";
                return (
                  <div key={e.id} className="support-email-log__row">
                    <span style={{ fontWeight: 700, color: ok ? "var(--success,#00897b)" : "var(--danger,#e53935)" }}>
                      {ok ? "送信" : "失敗"}
                    </span>
                    <span style={{ color: "var(--text-secondary,#5f635f)" }}>{e.direction === "outbound" ? "→" : "←"}</span>
                    <span className="mono">{e.to}</span>
                    <span className="support-email-log__subject">{e.subject}</span>
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>
        )}
      </div>
    </aside>
  );
}
